<template>
    <div class="assignment-state">
        <div
            class="assignment-state-buttons-group"
            v-if="!$authUser.isStudent()">
            <design-2021-apprentice-multiselect-component
                class="project-switcher with-border"
                id="select-project"
                v-if="!$authUser.isStudent() && groupIn.projects.length > 1"
                v-model="selectedProject"
                label="title"
                track-by="id"
                :options="groupIn.projects"
                :multiple-select="false"
                @input="redirectToNewProject()">
            </design-2021-apprentice-multiselect-component>

            <b-button
                class="btn-reopen-assignment"
                id="btn-reopen"
                v-if="closed"
                v-b-modal="'close-projects-modal'"
                v-b-tooltip.hover
                variant="danger-icon"
                :title="$t('portfolio.reopen_paper')">
                <i class="fa fa-fw fa-lock"></i>
            </b-button>

            <b-button
                id="btn-closed"
                v-if="!closed"
                v-b-modal="'close-projects-modal'"
                v-b-tooltip.hover
                :variant="closing ? 'warning-icon' : 'success-icon'"
                :class="closing ? 'btn-close-assignment' : 'btn-close-assignment'"
                :title="$t('portfolio.close_paper')">
                <i class="fa fa-fw fa-unlock"></i>
            </b-button>
        </div>
        <div v-else>
            <b-button
                class="btn-danger-icon-only btn-student-close-assignment-status"
                v-if="closed"
                v-b-tooltip.hover
                variant="danger"
                :title="$t('portfolio.paper_is_closed')">
                <i class="fa fa-fw fa-lock"></i>
            </b-button>
        </div>

        <design-2021-close-projects-modal-component
            :deadline-in="this.project.closes_at"
            ref="close-projects-modal"
            @remove="removeDeadline"
            @close="updateDeadline($event)">
        </design-2021-close-projects-modal-component>
    </div>
</template>

<script>
export default {
    name: 'design-2021-portfolio-show-open-close-component',
    props: {
        projectIn: {
            type: Object,
            required: true,
        },
        groupIn: {
            type: Object,
            required: false,
        },
    },
    data() {
        return {
            selectedProject: this.projectIn,
            project: this.projectIn,
        };
    },
    computed: {
        closed() {
            if (this.project.closes_at) {
                const closeDate = new Date(this.project.closes_at).getTime();
                const nowDate = new Date().getTime();

                return closeDate < nowDate;
            } else {
                return false;
            }
        },
        closing() {
            return !this.closed && !!this.project.closes_at;
        },
    },
    methods: {
        removeDeadline() {
            this.alert
                .confirm(
                    this.$t('portfolio.delete_deadline'),
                    this.$t('portfolio.are_you_sure_you_want_to_delete_the_deadline_for_this_project'),
                    this.$t('common.yes'),
                )
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }

                    this.alert.saving();
                    this.axios.delete(this.routes.setProjectDeadline(this.projectIn.id)).then(
                        (response) => {
                            this.project.closes_at = null;
                            this.alert.success();
                        },
                        (error) => {
                            this.alert.confirm(title);
                        },
                    );
                });
        },
        updateDeadline(deadline) {
            let dateTime = new Date(deadline);
            let date = `${dateTime.getFullYear()}-${String(dateTime.getMonth() + 1).padStart(2, '0')}-${String(dateTime.getDate()).padStart(2, '0')}`;
            let time = `${String(dateTime.getHours()).padStart(2, '0')}:${String(dateTime.getMinutes()).padStart(2, '0')}`;
            this.alert
                .confirm(
                    this.$t('portfolio.set_deadline'),
                    this.$t('paper.are_you_sure_you_want_to_set_the_deadline_for_this_project_at', {
                        date: date,
                        time: time,
                    }),
                    this.$t('common.yes'),
                )
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }
                    this.alert.saving();

                    let data = {
                        deadline: deadline,
                    };

                    this.axios.post(this.routes.setProjectDeadline(this.projectIn.id), data).then(
                        (response) => {
                            this.alert.success();
                            this.project.closes_at = response.data;
                        },
                        (error) => {
                            this.alert.confirm(title);
                        },
                    );
                });
        },
        updateStatus() {
            this.alert.saving();

            this.axios.post(this.routes.baseUrl() + '/portfolio/' + this.projectIn.id + '/setopenclose').then(
                (response) => {
                    this.alert.success();
                    this.closed = response.data.closed;
                },
                (error) => {
                    this.alert.confirm(title);
                },
            );
        },
        redirectToNewProject() {
            window.location = this.routes.portfolioShow(this.selectedProject.id);
        },
    },
};
</script>

<style scoped lang="scss">
.assignment-state {
    width: 100%;
}

.assignment-state-buttons-group {
    display: flex;
    align-items: center;
    align-self: center;
    justify-content: flex-end;
    column-gap: map-get($grid-gutter-spacing, 'fifteen');
    width: 100%;

    .btn {
        font-size: $base-font-size;

        &:not(.disabled) {
            i {
                font-size: $base-font-size;
                line-height: 1;
            }
        }
    }
}

.project-switcher {
    flex: 1 1 calc(100% - 55px);
    border-radius: 8px;
}

.btn-reopen-assignment {
    &:active,
    &:not(.disabled):active,
    &:not(.disabled):active:focus,
    &:active:focus {
        background-color: $danger;
        color: $white;
        outline: none;
    }

    &:hover {
        background-color: darken($danger, 10%);
    }
}

.btn-close-assignment {
    &:active,
    &:not(.disabled):active,
    &:not(.disabled):active:focus,
    &:active:focus {
        background-color: $success;
        color: $white;
        outline: none;
    }

    &:hover {
        background-color: darken($success, 10%);
    }
}

.btn-student-close-assignment-status {
    font-size: $base-font-size;

    &:hover {
        cursor: default;
    }
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .project-switcher {
        width: 200px;
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .assignment-state {
        width: auto;
    }
}
</style>
